import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import Filter from "../components/Report/Filter";
import TableFooter from "../components/Common/TableFooter";
import CommonExportButtons from "../components/Common/CommonExportButtons";
import PageSizeSelector from "../components/Common/PageSizeSelector";
import SkeletonTable from "../components/Common/SkeletonTable";
import { getPaymentsAction } from "../../store/actions/Payment/PaymentActions";

const Payment = () => {
  const dispatch = useDispatch();

  const { payments, loading } = useSelector((state) => state.payment);
  const paymentRows = payments?.rows || [];
  const totalPages = payments?.pages || 1;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filterQuery, setFilterQuery] = useState("");
  const [openMenu, setOpenMenu] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [customer, setCustomer] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderBy, setOrderBy] = useState("paidAt");
  const [orderByDir, setOrderByDir] = useState("desc");

  // Define skeleton table columns for payments
  const skeletonColumns = [
    { title: 'Customer Name', width: 120 },
    { title: 'Mobile', width: 100 },
    { title: 'Amount', width: 80 },
    { title: 'Payment Method', width: 120 },
    { title: 'Transaction Id', width: 140 },
    { title: 'Payment Status', width: 100 },
    { title: 'Date', width: 140 }
  ];

  const buildSearchQuery = () => {
    const customerId = customer ? customer : "";
    const paidAt =
      dateRange?.startDate && dateRange?.endDate
        ? `btw-${dateRange.startDate.format("YYYY/MM/DD")},${dateRange.endDate.format("YYYY/MM/DD")}`
        : "";
    const params = new URLSearchParams();
    if (customerId) params.append("filter_userId", customerId);
    if (paidAt) params.append("filter_paidAt", paidAt);
    if (paymentStatus) params.append("filter_paymentStatus", paymentStatus);
    if (orderBy) {
      params.append("orderBy", orderBy);
      params.append("orderByDir", orderByDir);
    }
    return params.toString();
  };

  const getPaymentStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'success':
      case 'completed':
      case 'paid':
        return '#28a745'; // Green for success
      case 'pending':
      case 'processing':
        return '#ffc107'; // Amber/Yellow for pending
      case 'failed':
      case 'cancelled':
      case 'rejected':
        return '#dc3545'; // Red for failed
      default:
        return '#6c757d'; // Gray for unknown status
    }
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderByDir(orderByDir === "desc" ? "asc" : "desc");
    } else {
      setOrderBy(field);
      setOrderByDir("desc");
    }
    setCurrentPage(1);
  };

  const onSubmit = async () => {
    setFilterQuery(buildSearchQuery());
    setCurrentPage(1);
  };

  // Clear filter button logic
  const isFilterActive = customer || (dateRange.startDate && dateRange.endDate) || paymentStatus || orderBy;
  const handleClearFilter = () => {
    setCustomer("");
    setDateRange({ startDate: null, endDate: null });
    setPaymentStatus("");
    setOrderBy("");
    setOrderByDir("desc");
    setFilterQuery("");
    setCurrentPage(1);
  };

  useEffect(() => {
    let query = buildSearchQuery();
    let effectivePageSize = pageSize;
    if (pageSize === 100000) {
      // 'All' option selected
      query = query ? `${query}&noPagination=true` : 'noPagination=true';
      effectivePageSize = 100000;
    }
    dispatch(getPaymentsAction(currentPage, effectivePageSize, "", "populate=userId,subscriptionPlanId&" + query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, filterQuery, orderBy, orderByDir]);

  return (
    <>
      <div className='form-head page-titles d-flex  align-items-center'>
        <div className='me-auto  d-lg-block'>
          <h4 className='mb-1'>Payment List</h4>
        </div>
      </div>
      <Filter
        setOpenMenu={setOpenMenu}
        openMenu={openMenu}
        onSubmit={onSubmit}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        setDateRange={setDateRange}
        setCustomer={setCustomer}
        customer={customer}
        paymentStatus={paymentStatus}
        setPaymentStatus={setPaymentStatus}
        isFilterActive={isFilterActive}
        handleClearFilter={handleClearFilter}
      />
      <div className="d-flex mb-2 align-items-center justify-content-between">
        <PageSizeSelector 
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrentPage={setCurrentPage}
          options={[10, 50, 100]}
        />
        <CommonExportButtons
          data={paymentRows}
          exportHeaders={[
            { label: 'Customer Name', key: 'userId.name' },
            { label: 'Mobile', key: 'userId.phone' },
            { label: 'Amount', key: 'amount' },
            { label: 'Payment Method', key: 'paymentMethod' },
            { label: 'Transaction Id', key: 'transactionId' },
            { label: 'Payment Status', key: 'paymentStatus' },
            { label: 'Date', key: 'paidAt' },
          ]}
          filename="payments"
          pdfTitle="Payment List"
          totalText="Total Payments"
          pdfOrientation="landscape"
          pdfHeaderColor={[40, 167, 69]}
          dateFields={['paidAt']}
          amountFields={['amount']}
        />
      </div>
      <div className="col-xl-12">
        <div className="table-responsive table-hover fs-14 order-list-table">
          {loading ? (
            <SkeletonTable columns={skeletonColumns} rows={8} />
          ) : (!loading && paymentRows.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220, width: '100%' }}>
              <div className="card shadow-sm border-0 p-4 text-center w-100" style={{ background: '#f8fafc' }}>
                <div style={{ fontSize: 48, color: '#adb5bd', marginBottom: 12 }}>
                  <i className="fa fa-search" />
                </div>
                <div style={{ fontWeight: 600, fontSize: 20, color: '#495057', marginBottom: 4 }}>
                  No Data Found
                </div>
                <div style={{ color: '#868e96', fontSize: 15 }}>
                  Try adjusting your filters or search criteria.
                </div>
              </div>
            </div>
          ) : (
            <table className='table display table-hover display mb-4 dataTablesCard card-table dataTable no-footer' id='paymentTable'>
              <thead>
                <tr role='row'>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('userId.name')}>
                    Customer Name
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'userId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.name' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'userId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.name' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('userId.phone')}>
                    Mobile
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'userId.phone' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.phone' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'userId.phone' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'userId.phone' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('amount')}>
                    Amount
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'amount' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'amount' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'amount' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'amount' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('paymentMethod')}>
                    Payment Method
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'paymentMethod' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'paymentMethod' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'paymentMethod' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'paymentMethod' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('transactionId')}>
                    Transaction Id
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'transactionId' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'transactionId' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'transactionId' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'transactionId' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('paymentStatus')}>
                    Payment Status
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'paymentStatus' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'paymentStatus' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'paymentStatus' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'paymentStatus' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('paidAt')}>
                    Date
                    <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                      <i className="fa fa-caret-up" style={{ color: orderBy === 'paidAt' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'paidAt' ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                      <i className="fa fa-caret-down" style={{ color: orderBy === 'paidAt' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'paidAt' ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paymentRows.map((item) => (
                  <tr key={item._id}>
                    <td>{item.userId.name}</td>
                    <td>{item.userId.phone || ""}</td>
                    <td>{"₹ " + Number(item.amount / 100).toLocaleString()}</td>
                    <td>{item.paymentMethod}</td>
                    <td>{item.transactionId}</td>
                    <td style={{ 
                      textTransform: "capitalize", 
                      color: getPaymentStatusColor(item.paymentStatus),
                      fontWeight: '600'
                    }}>
                      {item.paymentStatus}
                    </td>
                    <td>{moment(item.paidAt).format("DD/MM/YYYY, hh:mm A")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {(!loading && paymentRows.length > 0) && (
        <TableFooter
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalEntries={payments?.total || paymentRows.length}
          onPageChange={setCurrentPage}
        />
      )}
    </>
  );
};

export default Payment;
